import React from 'react';
import { ActionBase } from '../ActionBase';
import { Store } from '../Store';
import { copyObject } from 'common/copyObject';
import { CreateUser, callCreateUsersApi } from 'requests/RequestUser';
import { RegisterUserForm } from 'components/templates/UserRegister3Template1';
import { formatDate, DATE } from 'common/dateFormatter';
import { HttpStatus } from 'consts/HttpStatus';
import { ReactError } from 'consts/ReactError';

export type RegisterUserData = CreateUser & {
	actionError: string; // store内APIエラー
	isNotRobot: boolean; // ロボット確認
	isOnetimePasswordOk: boolean; // ワンタイムパスワード確認OKかどうか
	isV1ParamsOk: boolean; // 認可v1からのパラメータが存在するか
	isV2ParamsOk: boolean; // 認可v2からのパラメータが存在するか ※v1,v2どちらもfalseの場合は通常の登録
};
export const initialRegisterUserData: RegisterUserData = {
	actionError: '',
	isNotRobot: false,
	isOnetimePasswordOk: false,
	isV1ParamsOk: false,
	isV2ParamsOk: false,
	mailaddress_1: '',
	gid: '',
	pass: '',
	lastname: '',
	firstname: '',
	kana_lastname: '',
	kana_firstname: '',
	birth: '',
	sex: undefined,
	directmail_flg: 1,
	data_util_agree_flg: 1,
	tel: '',
	zip: '',
	pref_cd: '',
	address_2: '',
	address_3: '',
	address_4: '',
	nickname: '',
	country: 'JPN',
	family_type: 0,
	agree_flg: 0,
};

//////////////////////////////////////////////////
// StoreMailAddressAction
// 仮会員登録のページで入力されたメールアドレスをストアに保持するアクション
//////////////////////////////////////////////////
// Action
class StoreMailAddressAction extends ActionBase {
	registerUserData1: RegisterUserData;
}
// Action name
const StoreMailAddressActionName = StoreMailAddressAction.toString();

// Action generator
export const storeMailAddressGenerator = async (
	dispatch: React.Dispatch<ActionBase>,
	registerUserData1: RegisterUserData,
) => {
	const action = {
		action: StoreMailAddressActionName,
		registerUserData1,
	} as StoreMailAddressAction;

	dispatch(action);
};

// Reducer
const storeMailAddressReducer = (state: Store, action: ActionBase): Store => {
	const thisAction = action as StoreMailAddressAction;
	const newState: Store = copyObject(state);

	newState.registerUserData.isV1ParamsOk = thisAction.registerUserData1.isV1ParamsOk;
	newState.registerUserData.isV2ParamsOk = thisAction.registerUserData1.isV2ParamsOk;
	// ロボットではありません確認
	newState.registerUserData.isNotRobot = thisAction.registerUserData1.isNotRobot;
	// メールアドレス
	newState.registerUserData.mailaddress_1 = thisAction.registerUserData1.mailaddress_1;

	return newState;
};

//////////////////////////////////////////////////
// StoreOnetimePasswordAction
// 仮パスワード入力のページで入力されたワンタイムパスワード確認結果をストアに保持するアクション
//////////////////////////////////////////////////
// Action
class StoreOnetimePasswordAction extends ActionBase {
	registerUserData2: RegisterUserData;
}
// Action name
const StoreOnetimePasswordActionName = StoreOnetimePasswordAction.toString();

// Action generator
export const storeOnetimePasswordGenerator = async (
	dispatch: React.Dispatch<ActionBase>,
	registerUserData2: RegisterUserData,
) => {
	const action = {
		action: StoreOnetimePasswordActionName,
		registerUserData2,
	} as StoreOnetimePasswordAction;

	dispatch(action);
};

// Reducer
const storeOnetimePasswordReducer = (state: Store, action: ActionBase): Store => {
	const thisAction = action as StoreOnetimePasswordAction;
	const newState: Store = copyObject(state);

	// ワンタイムパスワード確認
	newState.registerUserData.isOnetimePasswordOk = thisAction.registerUserData2.isOnetimePasswordOk;

	return newState;
};

//////////////////////////////////////////////////
// StoreRegisterUserData1Action
// 本会員登録1のページで入力された値をストアに保持するアクション
//////////////////////////////////////////////////
// Action
class StoreRegisterUserData1Action extends ActionBase {
	registerUserData3_1: RegisterUserForm;
}
// Action name
const StoreRegisterUserData1ActionName = StoreRegisterUserData1Action.toString();

// Action generator
export const storeRegisterUserData1Generator = async (
	dispatch: React.Dispatch<ActionBase>,
	registerUserData3_1: RegisterUserData,
) => {
	const action = {
		action: StoreRegisterUserData1ActionName,
		registerUserData3_1,
	} as StoreRegisterUserData1Action;

	dispatch(action);
};

// Reducer
const storeRegisterUserData1Reducer = (state: Store, action: ActionBase): Store => {
	const thisAction = action as StoreRegisterUserData1Action;
	const newState: Store = copyObject(state);
	// Gakken ID
	newState.registerUserData.gid = thisAction.registerUserData3_1.gid;
	// パスワード
	newState.registerUserData.pass = thisAction.registerUserData3_1.pass;
	// 姓
	newState.registerUserData.lastname = thisAction.registerUserData3_1.lastname;
	// 名
	newState.registerUserData.firstname = thisAction.registerUserData3_1.firstname;
	// セイ
	newState.registerUserData.kana_lastname = thisAction.registerUserData3_1.kana_lastname;
	// メイ
	newState.registerUserData.kana_firstname = thisAction.registerUserData3_1.kana_firstname;
	// 生年月日
	const isBirthExist =
		!!thisAction.registerUserData3_1.birthYear &&
		!!thisAction.registerUserData3_1.birthMonth &&
		!!thisAction.registerUserData3_1.birthDate;
	newState.registerUserData.birth = isBirthExist
		? formatDate(
				new Date(
					Number(thisAction.registerUserData3_1.birthYear),
					Number(thisAction.registerUserData3_1.birthMonth) - 1,
					Number(thisAction.registerUserData3_1.birthDate),
				),
				DATE,
		  )
		: undefined;
	// 性別
	newState.registerUserData.sex = thisAction.registerUserData3_1.sex;
	// GakkenIDのメルマガを受け取る
	newState.registerUserData.directmail_flg = thisAction.registerUserData3_1.directmail_flg;
	// サービス向上のためデータ活用に同意する
	newState.registerUserData.data_util_agree_flg = thisAction.registerUserData3_1.data_util_agree_flg;

	return newState;
};

//////////////////////////////////////////////////
// StoreRegisterUserData2Action
// 本会員登録2のページで入力された値をストアに保持するアクション
//////////////////////////////////////////////////
// Action
class StoreRegisterUserData2Action extends ActionBase {
	registerUserData3_2: RegisterUserData;
}
// Action name
const StoreRegisterUserData2ActionName = StoreRegisterUserData2Action.toString();

// Action generator
export const storeRegisterUserData2Generator = async (
	dispatch: React.Dispatch<ActionBase>,
	registerUserData3_2: RegisterUserData,
) => {
	const action = {
		action: StoreRegisterUserData2ActionName,
		registerUserData3_2,
	} as StoreRegisterUserData2Action;

	dispatch(action);
};

// Reducer
const storeRegisterUserData2Reducer = (state: Store, action: ActionBase): Store => {
	const thisAction = action as StoreRegisterUserData2Action;
	const newState: Store = copyObject(state);
	// 電話番号
	newState.registerUserData.tel = thisAction.registerUserData3_2.tel;
	// 郵便番号
	newState.registerUserData.zip = thisAction.registerUserData3_2.zip;
	// 都道府県
	newState.registerUserData.pref_cd = thisAction.registerUserData3_2.pref_cd;
	// 市区町村
	newState.registerUserData.address_2 = thisAction.registerUserData3_2.address_2;
	// 町名・番地
	newState.registerUserData.address_3 = thisAction.registerUserData3_2.address_3;
	// マンション名
	newState.registerUserData.address_4 = thisAction.registerUserData3_2.address_4;
	// ニックネーム
	newState.registerUserData.nickname = thisAction.registerUserData3_2.nickname;
	// 国
	newState.registerUserData.country = thisAction.registerUserData3_2.country;

	return newState;
};

//////////////////////////////////////////////////
// RegisterUserAction
//////////////////////////////////////////////////
// Action
class RegisterUserAction extends ActionBase {
	registerUserData4: RegisterUserData;
}
// Action name
const RegisterUserActionName = RegisterUserAction.toString();

// Action generator
export const registerUserGenerator = async (
	dispatch: React.Dispatch<ActionBase>,
	registerUserData4: RegisterUserData,
	updateSiteCd: string,
) => {
	const createUser: CreateUser = {
		mailaddress_1: registerUserData4.mailaddress_1,
		gid: registerUserData4.gid,
		pass: registerUserData4.pass,
		lastname: registerUserData4.lastname,
		firstname: registerUserData4.firstname,
		kana_lastname: registerUserData4.kana_lastname,
		kana_firstname: registerUserData4.kana_firstname,
		birth: registerUserData4.birth,
		sex: registerUserData4.sex,
		directmail_flg: registerUserData4.directmail_flg,
		data_util_agree_flg: registerUserData4.data_util_agree_flg,
		tel: registerUserData4.tel,
		zip: registerUserData4.zip,
		pref_cd: registerUserData4.pref_cd,
		address_2: registerUserData4.address_2,
		address_3: registerUserData4.address_3,
		address_4: registerUserData4.address_4,
		nickname: registerUserData4.nickname,
		country: registerUserData4.country,
		family_type: 1, // 家族タイプ
		agree_flg: 1, // 規約同意
		update_site_cd: updateSiteCd,
	};

	const result = await callCreateUsersApi({ user_list: [createUser] });
	if (result.status === HttpStatus.GATEWAY_TIMEOUT) {
		registerUserData4.actionError = ReactError.E9998;
	}
	if (result.status !== HttpStatus.OK) {
		registerUserData4.actionError = ReactError.E9999;
	}

	const action = {
		action: RegisterUserActionName,
		registerUserData4,
	} as RegisterUserAction;

	dispatch(action);
};

// Reducer
const registerUserReducer = (state: Store, action: ActionBase): Store => {
	const thisAction = action as RegisterUserAction;
	const newState: Store = copyObject(state);
	newState.registerUserData = thisAction.registerUserData4;
	return newState;
};

//////////////////////////////////////////////////
// ClearUserDataAction
// 保持しているユーザデータを初期化する
//////////////////////////////////////////////////
// Action
class ClearUserDataAction extends ActionBase {
	clearUserDataAction: RegisterUserData;
}
// Action name
const ClearUserDataActionName = ClearUserDataAction.toString();

// Action generator
export const clearUserDataGenerator = async (
	dispatch: React.Dispatch<ActionBase>,
	clearUserDataAction: RegisterUserData,
) => {
	const action = {
		action: ClearUserDataActionName,
		clearUserDataAction,
	} as ClearUserDataAction;

	dispatch(action);
};

// Reducer
const clearUserDataReducer = (state: Store, action: ActionBase): Store => {
	const thisAction = action as ClearUserDataAction;
	const newState: Store = copyObject(state);
	newState.registerUserData = thisAction.clearUserDataAction;
	return newState;
};

////////////////////////////////////////////////////////////////////////////////
// Reduce functions finder
////////////////////////////////////////////////////////////////////////////////
export const RegisterUserReduceFunction = (state: Store, action: ActionBase): Store => {
	switch (action.action) {
		case StoreMailAddressActionName:
			return storeMailAddressReducer(state, action);
		case StoreOnetimePasswordActionName:
			return storeOnetimePasswordReducer(state, action);
		case StoreRegisterUserData1ActionName:
			return storeRegisterUserData1Reducer(state, action);
		case StoreRegisterUserData2ActionName:
			return storeRegisterUserData2Reducer(state, action);
		case RegisterUserActionName:
			return registerUserReducer(state, action);
		case ClearUserDataActionName:
			return clearUserDataReducer(state, action);
	}
	return null;
};
